import { decCmp, decSum, formatDec, isDecimalText } from '@felix/domain';
import { ActionIcon, Autocomplete, Button, Select, Textarea, TextInput } from '@mantine/core';
import {
  IconBuildingWarehouse,
  IconChevronDown,
  IconChevronUp,
  IconDatabase,
  IconDroplet,
  IconFileInvoice,
  IconFlask,
  IconPlus,
  IconShip,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import { PortPicker } from '../../inputs/PortPicker.js';
import { TimeInput } from '../../inputs/TimeInput.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import classes from './Ledger.module.css';
import { useLedgerRecords } from './ledger-state.js';
import {
  DecisionModal,
  FileField,
  joinLocal,
  Notices,
  OfficeNote,
  orNull,
  RecordPicker,
  Section,
  StatusBadge,
  StatusBanner,
  splitLocal,
  WorkflowButtons,
  workflowActions,
} from './ledger-ui.js';
import type { FuelSource } from './source.js';
import type { FuelContextData, FuelDocumentKind, FuelRecordDetail } from './types.js';

const GRADES = ['RMG 380', 'RME 180', 'RMD 80', 'RMK 500', 'DMA', 'DMB', 'DMZ'];
const CURRENCIES = ['USD', 'EUR', 'GBP', 'TRY', 'SGD', 'AED', 'NOK', 'JPY', 'CNY'];
const DELIVERY = ['barge', 'truck', 'pipeline', 'ship_to_ship'] as const;
const SAMPLE_TYPES = ['marpol', 'supplier', 'ship', 'lab'] as const;

interface Line {
  tankId: string | null;
  massMt: string;
  remarks: string;
}

export interface BunkeringValues {
  portName: string;
  portCode: string | null;
  voyageId: string | null;
  unlinkedReason: string;
  startDay: string | null;
  startTime: string | null;
  endDay: string | null;
  endTime: string | null;
  responsibleId: string | null;
  deliveryMethod: string | null;
  supplier: string;
  barge: string;
  bdnNo: string;
  bdnDate: string | null;
  fuelTypeId: string | null;
  grade: string;
  batchCode: string;
  density15: string;
  sulphurPct: string;
  viscosityCst: string;
  flashPointC: string;
  bdnMt: string;
  shipMt: string;
  acceptedMt: string;
  lines: Line[];
  sampleNo: string;
  sealNo: string;
  sampleType: string | null;
  sampleLocation: string;
  laboratory: string;
  analysisResult: string;
  unitPrice: string;
  currency: string | null;
  extraCost: string;
  invoiceNo: string;
  fxRate: string;
  fxDate: string | null;
  differenceNote: string;
  remarks: string;
  altComponent: string;
  altRatio: string;
  altOrigin: string;
  altCertificate: string;
}

export const EMPTY_BUNKERING: BunkeringValues = {
  portName: '',
  portCode: null,
  voyageId: null,
  unlinkedReason: '',
  startDay: null,
  startTime: null,
  endDay: null,
  endTime: null,
  responsibleId: null,
  deliveryMethod: null,
  supplier: '',
  barge: '',
  bdnNo: '',
  bdnDate: null,
  fuelTypeId: null,
  grade: '',
  batchCode: '',
  density15: '',
  sulphurPct: '',
  viscosityCst: '',
  flashPointC: '',
  bdnMt: '',
  shipMt: '',
  acceptedMt: '',
  lines: [{ tankId: null, massMt: '', remarks: '' }],
  sampleNo: '',
  sealNo: '',
  sampleType: null,
  sampleLocation: '',
  laboratory: '',
  analysisResult: '',
  unitPrice: '',
  currency: 'USD',
  extraCost: '',
  invoiceNo: '',
  fxRate: '',
  fxDate: null,
  differenceNote: '',
  remarks: '',
  altComponent: '',
  altRatio: '',
  altOrigin: '',
  altCertificate: '',
};

const str = (value: unknown) => (typeof value === 'string' ? value : '');
const opt = (value: unknown) => (typeof value === 'string' && value !== '' ? value : null);

/** Kaydın girdisi → form değerleri. */
export function bunkeringValuesOf(input: Record<string, unknown>): BunkeringValues {
  const start = splitLocal(input.startedLocal);
  const end = splitLocal(input.endedLocal);
  const alt = (input.alternative ?? {}) as Record<string, unknown>;
  const lines = Array.isArray(input.lines)
    ? (input.lines as Record<string, unknown>[]).map((l) => ({
        tankId: opt(l.tankId),
        massMt: str(l.massMt),
        remarks: str(l.remarks),
      }))
    : [];
  return {
    portName: str(input.portName),
    portCode: opt(input.portCode),
    voyageId: opt(input.voyageId),
    unlinkedReason: str(input.unlinkedReason),
    startDay: start.day,
    startTime: start.time,
    endDay: end.day,
    endTime: end.time,
    responsibleId: opt(input.responsibleId),
    deliveryMethod: opt(input.deliveryMethod),
    supplier: str(input.supplier),
    barge: str(input.barge),
    bdnNo: str(input.bdnNo),
    bdnDate: opt(input.bdnDate),
    fuelTypeId: opt(input.fuelTypeId),
    grade: str(input.grade),
    batchCode: str(input.batchCode),
    density15: str(input.density15),
    sulphurPct: str(input.sulphurPct),
    viscosityCst: str(input.viscosityCst),
    flashPointC: str(input.flashPointC),
    bdnMt: str(input.bdnMt),
    shipMt: str(input.shipMt),
    acceptedMt: str(input.acceptedMt),
    lines: lines.length > 0 ? lines : [{ tankId: null, massMt: '', remarks: '' }],
    sampleNo: str(input.sampleNo),
    sealNo: str(input.sealNo),
    sampleType: opt(input.sampleType),
    sampleLocation: str(input.sampleLocation),
    laboratory: str(input.laboratory),
    analysisResult: str(input.analysisResult),
    unitPrice: str(input.unitPrice),
    currency: opt(input.currency),
    extraCost: str(input.extraCost),
    invoiceNo: str(input.invoiceNo),
    fxRate: str(input.fxRate),
    fxDate: opt(input.fxDate),
    differenceNote: str(input.differenceNote),
    remarks: str(input.remarks),
    altComponent: str(alt.component),
    altRatio: str(alt.ratioPct),
    altOrigin: str(alt.origin),
    altCertificate: str(alt.certificate),
  };
}

/** Form değerleri → kaydın girdisi (domain şeması; boş alan `null`). */
export function bunkeringInputOf(v: BunkeringValues): Record<string, unknown> {
  const alternative = {
    component: orNull(v.altComponent),
    ratioPct: orNull(v.altRatio),
    origin: orNull(v.altOrigin),
    certificate: orNull(v.altCertificate),
  };
  return {
    portName: orNull(v.portName),
    portCode: v.portCode,
    voyageId: v.voyageId,
    unlinkedReason: v.voyageId ? null : orNull(v.unlinkedReason),
    startedLocal: joinLocal(v.startDay, v.startTime),
    endedLocal: joinLocal(v.endDay, v.endTime),
    responsibleId: v.responsibleId,
    deliveryMethod: v.deliveryMethod,
    supplier: orNull(v.supplier),
    barge: orNull(v.barge),
    bdnNo: orNull(v.bdnNo),
    bdnDate: v.bdnDate,
    fuelTypeId: v.fuelTypeId,
    grade: orNull(v.grade),
    batchCode: orNull(v.batchCode),
    density15: orNull(v.density15),
    sulphurPct: orNull(v.sulphurPct),
    viscosityCst: orNull(v.viscosityCst),
    flashPointC: orNull(v.flashPointC),
    bdnMt: orNull(v.bdnMt),
    shipMt: orNull(v.shipMt),
    acceptedMt: orNull(v.acceptedMt),
    lines: v.lines
      .filter((l) => l.tankId !== null)
      .map((l) => ({ tankId: l.tankId, massMt: orNull(l.massMt), remarks: orNull(l.remarks) })),
    sampleNo: orNull(v.sampleNo),
    sealNo: orNull(v.sealNo),
    sampleType: v.sampleType,
    sampleLocation: orNull(v.sampleLocation),
    laboratory: orNull(v.laboratory),
    analysisResult: orNull(v.analysisResult),
    alternative: Object.values(alternative).some((x) => x !== null) ? alternative : null,
    unitPrice: orNull(v.unitPrice),
    currency: v.currency,
    extraCost: orNull(v.extraCost),
    invoiceNo: orNull(v.invoiceNo),
    fxRate: orNull(v.fxRate),
    fxDate: v.fxDate,
    differenceNote: orNull(v.differenceNote),
    remarks: orNull(v.remarks),
  };
}

/** Dağılım toplamı (yalnız geçerli ondalıklar). */
export function linesTotal(lines: readonly Line[]): string {
  return formatDec(decSum(lines.map((l) => (isDecimalText(l.massMt) ? l.massMt : '0'))), 2);
}

export interface BunkeringPanelProps {
  source: FuelSource;
  vesselId: string | null;
  onVessel?: (data: FuelContextData) => void;
}

/**
 * Yakıt Alımları sekmesi (ortağın 03-yakit-alimlari.jpeg tasarımı; ortak belgesi §7): Operasyon,
 * Tedarik ve Belge, Yakıt ve Miktar, Tank Dağılımı, Numune–Analiz–Fatura bölümleri; altta Taslak
 * Kaydet ve Onaya Gönder. BDN, gemi ölçümü ve kabul miktarı ayrı; dağılım toplamı kabule eşit.
 * Onaylanınca kabul miktarı tanklara tek işlemle işlenir. Ofiste salt okunur.
 */
export function BunkeringPanel({ source, vesselId, onVessel }: BunkeringPanelProps) {
  const { t } = useTranslation();
  const ledger = useLedgerRecords(source, vesselId, 'bunkering', onVessel);
  const { context, detail } = ledger;
  const [values, setValues] = useState<BunkeringValues>(EMPTY_BUNKERING);
  const [more, setMore] = useState(false);
  const [modal, setModal] = useState<'approve' | 'return' | 'reverse' | null>(null);

  useEffect(() => {
    setValues(detail ? bunkeringValuesOf(detail.input) : EMPTY_BUNKERING);
  }, [detail]);

  const editorSide = source.ledgerEditor !== undefined;
  const status = ledger.selectedId === null ? 'new' : (detail?.status ?? 'draft');
  const editable = editorSide && (status === 'new' || status === 'draft' || status === 'returned');
  const set = <K extends keyof BunkeringValues>(key: K, value: BunkeringValues[K]) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const tankOptions = useMemo(
    () =>
      (context?.tanks ?? []).map((tank) => ({
        value: tank.id,
        label: `${tank.name} (${tank.code})`,
      })),
    [context],
  );
  const fuelOptions = (context?.fuelTypes ?? []).map((f) => ({ value: f.id, label: f.code }));
  const crewOptions = (context?.crew ?? []).map((p) => ({ value: p.id, label: p.name }));
  const voyageOptions = (context?.voyages ?? []).map((v) => ({ value: v.id, label: v.voyageNo }));
  const total = linesTotal(values.lines);
  const mismatch =
    isDecimalText(values.acceptedMt) &&
    values.lines.some((l) => l.massMt !== '') &&
    decCmp(total, values.acceptedMt) !== 0;
  const issue = (path: string) => {
    const code = ledger.fieldIssues[path];
    return code ? t(`fuel.fieldErrors.${code}`, { defaultValue: code }) : null;
  };

  const doc = (kind: FuelDocumentKind) =>
    detail?.documents.find((d) => d.document === kind && !ledger.removed.includes(d.id)) ?? null;
  const fileField = (kind: FuelDocumentKind, label: string, required = false) => (
    <FileField
      label={label}
      required={required}
      file={doc(kind)}
      pending={ledger.pendingFiles[kind] ?? null}
      // Analiz ve fatura onaydan sonra da eklenebilir (ortak belgesi §7); BDN içerik hash'indedir
      editable={editorSide && (editable || (kind !== 'bdn' && status === 'approved'))}
      onPick={(file) => ledger.setPendingFiles((prev) => ({ ...prev, [kind]: file }))}
      onRemove={() => {
        if (ledger.pendingFiles[kind]) {
          ledger.setPendingFiles((prev) => {
            const next = { ...prev };
            delete next[kind];
            return next;
          });
          return;
        }
        const existing = doc(kind);
        if (existing) ledger.setRemoved((prev) => [...prev, existing.id]);
      }}
      error={kind === 'bdn' ? issue('bdnFile') : null}
    />
  );

  if (ledger.state === 'loading') {
    return <TableSkeleton columns={5} rows={8} label={t('fuel.bunkering.title')} />;
  }
  if (ledger.state === 'error') {
    return (
      <LoadError
        code={ledger.loadCode ?? 'GENERIC'}
        onRetry={() => void ledger.reload()}
        title={t('fuel.bunkering.title')}
      />
    );
  }

  const actions = workflowActions(status, context?.can ?? EMPTY_CAN, editorSide);
  const input = () => bunkeringInputOf(values);
  const correctsNo = detail?.correctsId
    ? (ledger.records.find((r) => r.id === detail.correctsId)?.recordNo ?? null)
    : null;
  const roleOf = (tankId: string | null) => {
    const tank = context?.tanks.find((x) => x.id === tankId);
    return tank ? t(`fuel.tanks.roles.${tank.role}`) : '';
  };

  return (
    <section className={classes.card} aria-label={t('fuel.bunkering.title')}>
      <div className={classes.head}>
        <h2 className={classes.title}>{t('fuel.bunkering.title')}</h2>
        <div className={classes.picker}>
          {detail ? <StatusBadge status={detail.status} /> : null}
          <RecordPicker
            records={ledger.records}
            selectedId={ledger.selectedId}
            onSelect={(id) => ledger.select(id)}
            onNew={() => ledger.select(null)}
            canCreate={editorSide && (context?.can.create ?? false)}
          />
        </div>
      </div>
      <div className={classes.body}>
        {editorSide ? null : <OfficeNote />}
        <StatusBanner detail={detail} people={ledger.people} correctsNo={correctsNo} />
        <Notices error={ledger.error} done={ledger.done} />

        <Section
          icon={<IconFileInvoice size={22} />}
          title={t('fuel.bunkering.sections.operation')}
        >
          <div className={classes.grid5}>
            <PortPicker
              label={t('fuel.bunkering.fields.port')}
              withAsterisk
              disabled={!editable}
              value={{ name: values.portName, code: values.portCode }}
              onChange={(next) =>
                setValues((prev) => ({ ...prev, portName: next.name, portCode: next.code }))
              }
              error={issue('portName')}
            />
            <Select
              label={t('fuel.bunkering.fields.voyage')}
              withAsterisk
              data={voyageOptions}
              value={values.voyageId}
              onChange={(value) => set('voyageId', value)}
              readOnly={!editable}
              clearable={editable}
              error={issue('voyageId')}
            />
            <DateTime
              label={t('fuel.bunkering.fields.start')}
              day={values.startDay}
              time={values.startTime}
              readOnly={!editable}
              onDay={(v) => set('startDay', v)}
              onTime={(v) => set('startTime', v)}
              error={issue('startedLocal')}
            />
            <DateTime
              label={t('fuel.bunkering.fields.end')}
              day={values.endDay}
              time={values.endTime}
              readOnly={!editable}
              onDay={(v) => set('endDay', v)}
              onTime={(v) => set('endTime', v)}
              error={issue('endedLocal')}
            />
            <Select
              label={t('fuel.bunkering.fields.responsible')}
              withAsterisk
              data={crewOptions}
              value={values.responsibleId}
              onChange={(value) => set('responsibleId', value)}
              readOnly={!editable}
              searchable
              error={issue('responsibleId')}
            />
            {values.voyageId === null ? (
              <TextInput
                className={classes.span5}
                label={t('fuel.bunkering.fields.unlinkedReason')}
                withAsterisk
                readOnly={!editable}
                value={values.unlinkedReason}
                onChange={(e) => set('unlinkedReason', e.currentTarget.value)}
                error={issue('unlinkedReason')}
              />
            ) : null}
          </div>
        </Section>

        <Section icon={<IconShip size={22} />} title={t('fuel.bunkering.sections.supply')}>
          <div className={classes.grid5}>
            <TextInput
              label={t('fuel.bunkering.fields.supplier')}
              withAsterisk
              readOnly={!editable}
              value={values.supplier}
              onChange={(e) => set('supplier', e.currentTarget.value)}
              error={issue('supplier')}
            />
            <TextInput
              label={t('fuel.bunkering.fields.barge')}
              withAsterisk
              readOnly={!editable}
              value={values.barge}
              onChange={(e) => set('barge', e.currentTarget.value)}
              error={issue('barge')}
            />
            <TextInput
              label={t('fuel.bunkering.fields.bdnNo')}
              withAsterisk
              readOnly={!editable}
              value={values.bdnNo}
              onChange={(e) => set('bdnNo', e.currentTarget.value)}
              error={issue('bdnNo')}
            />
            <DayInput
              label={t('fuel.bunkering.fields.bdnDate')}
              withAsterisk
              readOnly={!editable}
              value={values.bdnDate}
              onChange={(v) => set('bdnDate', v)}
              error={issue('bdnDate')}
            />
            {fileField('bdn', t('fuel.bunkering.fields.bdnFile'), true)}
          </div>
        </Section>

        <Section icon={<IconDroplet size={22} />} title={t('fuel.bunkering.sections.fuel')}>
          <div className={classes.grid7}>
            <Select
              label={t('fuel.bunkering.fields.fuelType')}
              withAsterisk
              data={fuelOptions}
              value={values.fuelTypeId}
              onChange={(value) => set('fuelTypeId', value)}
              readOnly={!editable}
              error={issue('fuelTypeId')}
            />
            <Autocomplete
              label={t('fuel.bunkering.fields.grade')}
              data={GRADES}
              readOnly={!editable}
              value={values.grade}
              onChange={(value) => set('grade', value)}
              rightSection={<IconChevronDown size={16} aria-hidden />}
              rightSectionPointerEvents="none"
            />
            <div className={classes.unitInput}>
              <TextInput
                label={t('fuel.bunkering.fields.density')}
                withAsterisk
                inputMode="decimal"
                readOnly={!editable}
                value={values.density15}
                onChange={(e) => set('density15', e.currentTarget.value)}
                error={issue('density15')}
              />
              <span className={classes.unitTag} aria-hidden>
                t/m³
              </span>
            </div>
            <Amount
              label={t('fuel.bunkering.fields.sulphur')}
              value={values.sulphurPct}
              readOnly={!editable}
              onChange={(v) => set('sulphurPct', v)}
              error={issue('sulphurPct')}
              required
            />
            <Amount
              label={t('fuel.bunkering.fields.bdnMt')}
              value={values.bdnMt}
              readOnly={!editable}
              onChange={(v) => set('bdnMt', v)}
              error={issue('bdnMt')}
              required
            />
            <Amount
              label={t('fuel.bunkering.fields.shipMt')}
              value={values.shipMt}
              readOnly={!editable}
              onChange={(v) =>
                // Kabul miktarı boşsa ya da gemi ölçümünü izliyorsa gemi ölçümüyle önerilir
                setValues((prev) => ({
                  ...prev,
                  shipMt: v,
                  acceptedMt:
                    prev.acceptedMt === '' || prev.acceptedMt === prev.shipMt ? v : prev.acceptedMt,
                }))
              }
              error={issue('shipMt')}
              required
            />
            <Amount
              label={t('fuel.bunkering.fields.acceptedMt')}
              value={values.acceptedMt}
              readOnly={!editable}
              onChange={(v) => set('acceptedMt', v)}
              error={issue('acceptedMt')}
              required
              computed
            />
          </div>
          {detail?.bdnDifference && detail.bdnDifference !== '0' ? (
            <p className={classes.hint}>
              {t('fuel.bunkering.bdnDifference', { value: detail.bdnDifference })}
            </p>
          ) : null}
        </Section>

        <Section
          icon={<IconDatabase size={22} />}
          title={t('fuel.bunkering.sections.distribution')}
          action={
            editable ? (
              <Button
                size="xs"
                variant="outline"
                className={classes.addButton}
                leftSection={<IconPlus size={14} />}
                onClick={() =>
                  set('lines', [...values.lines, { tankId: null, massMt: '', remarks: '' }])
                }
              >
                {t('fuel.bunkering.addTank')}
              </Button>
            ) : undefined
          }
        >
          <div className={classes.tableWrap}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>{t('fuel.bunkering.fields.tankName')}</th>
                  <th>{t('fuel.bunkering.fields.tankType')}</th>
                  <th>{t('fuel.bunkering.fields.mass')}</th>
                  <th>{t('fuel.bunkering.fields.remarks')}</th>
                  {editable ? <th style={{ width: 48 }} /> : null}
                </tr>
              </thead>
              <tbody>
                {values.lines.map((line, index) => {
                  const n = index + 1;
                  const update = (patch: Partial<Line>) =>
                    set(
                      'lines',
                      values.lines.map((l, i) => (i === index ? { ...l, ...patch } : l)),
                    );
                  return (
                    // biome-ignore lint/suspicious/noArrayIndexKey: satırın kimliği sırasıdır
                    <tr key={index}>
                      <td className={classes.center}>{n}</td>
                      <td>
                        <Select
                          aria-label={`${t('fuel.bunkering.fields.tankName')} ${n}`}
                          data={tankOptions}
                          value={line.tankId}
                          onChange={(value) => update({ tankId: value })}
                          readOnly={!editable}
                        />
                      </td>
                      <td>
                        <TextInput
                          aria-label={`${t('fuel.bunkering.fields.tankType')} ${n}`}
                          value={roleOf(line.tankId)}
                          readOnly
                        />
                      </td>
                      <td>
                        <TextInput
                          aria-label={`${t('fuel.bunkering.fields.mass')} ${n}`}
                          inputMode="decimal"
                          value={line.massMt}
                          readOnly={!editable}
                          onChange={(e) => update({ massMt: e.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <TextInput
                          aria-label={`${t('fuel.bunkering.fields.remarks')} ${n}`}
                          value={line.remarks}
                          readOnly={!editable}
                          onChange={(e) => update({ remarks: e.currentTarget.value })}
                        />
                      </td>
                      {editable ? (
                        <td className={classes.center}>
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            aria-label={t('fuel.bunkering.removeLine', { n })}
                            onClick={() =>
                              set(
                                'lines',
                                values.lines.length === 1
                                  ? [{ tankId: null, massMt: '', remarks: '' }]
                                  : values.lines.filter((_, i) => i !== index),
                              )
                            }
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className={classes.totalRow}>
            <span className={classes.totalLabel}>{t('fuel.bunkering.total')}</span>
            <span
              className={`${classes.totalValue} ${mismatch ? classes.totalMismatch : ''}`}
              aria-live="polite"
            >
              {total} MT
            </span>
          </div>
          {mismatch || issue('lines') ? (
            <p className={classes.hint} role="alert" style={{ color: 'var(--felix-danger-fg)' }}>
              {t('fuel.fieldErrors.DISTRIBUTION_MISMATCH')}
            </p>
          ) : null}
        </Section>

        <Section icon={<IconFlask size={22} />} title={t('fuel.bunkering.sections.sample')}>
          <div className={classes.grid6}>
            <TextInput
              label={t('fuel.bunkering.fields.sampleNo')}
              withAsterisk
              readOnly={!editable}
              value={values.sampleNo}
              onChange={(e) => set('sampleNo', e.currentTarget.value)}
              error={issue('sampleNo')}
            />
            <TextInput
              label={t('fuel.bunkering.fields.sealNo')}
              withAsterisk
              readOnly={!editable}
              value={values.sealNo}
              onChange={(e) => set('sealNo', e.currentTarget.value)}
              error={issue('sealNo')}
            />
            {fileField('lab_report', t('fuel.bunkering.fields.labReport'))}
            <Amount
              label={t('fuel.bunkering.fields.unitPrice')}
              value={values.unitPrice}
              readOnly={!editable}
              onChange={(v) => set('unitPrice', v)}
              error={issue('unitPrice')}
              required
            />
            <Select
              label={t('fuel.bunkering.fields.currency')}
              withAsterisk
              data={CURRENCIES}
              value={values.currency}
              onChange={(value) => set('currency', value)}
              readOnly={!editable}
              error={issue('currency')}
            />
            {fileField('invoice', t('fuel.bunkering.fields.invoice'))}
          </div>
        </Section>

        <section className={classes.section} aria-label={t('fuel.bunkering.sections.more')}>
          <button
            type="button"
            className={classes.sectionHead}
            style={{ border: 0, width: '100%', cursor: 'pointer', font: 'inherit' }}
            aria-expanded={more}
            onClick={() => setMore((open) => !open)}
          >
            <span className={classes.sectionTitle}>
              <IconBuildingWarehouse size={22} className={classes.sectionIcon} aria-hidden />
              {t('fuel.bunkering.sections.more')}
            </span>
            {more ? (
              <IconChevronUp size={18} aria-hidden />
            ) : (
              <IconChevronDown size={18} aria-hidden />
            )}
          </button>
          {more ? (
            <div className={classes.grid5}>
              <Select
                label={t('fuel.bunkering.fields.deliveryMethod')}
                data={DELIVERY.map((d) => ({
                  value: d,
                  label: t(`fuel.bunkering.deliveryMethods.${d}`),
                }))}
                value={values.deliveryMethod}
                onChange={(v) => set('deliveryMethod', v)}
                readOnly={!editable}
                clearable={editable}
              />
              <TextInput
                label={t('fuel.bunkering.fields.batchCode')}
                readOnly={!editable}
                value={values.batchCode}
                onChange={(e) => set('batchCode', e.currentTarget.value)}
                placeholder={values.bdnNo}
              />
              <Amount
                label={t('fuel.bunkering.fields.viscosity')}
                value={values.viscosityCst}
                readOnly={!editable}
                onChange={(v) => set('viscosityCst', v)}
                error={issue('viscosityCst')}
              />
              <Amount
                label={t('fuel.bunkering.fields.flashPoint')}
                value={values.flashPointC}
                readOnly={!editable}
                onChange={(v) => set('flashPointC', v)}
                error={issue('flashPointC')}
              />
              <Select
                label={t('fuel.bunkering.fields.sampleType')}
                data={SAMPLE_TYPES.map((d) => ({
                  value: d,
                  label: t(`fuel.bunkering.sampleTypes.${d}`),
                }))}
                value={values.sampleType}
                onChange={(v) => set('sampleType', v)}
                readOnly={!editable}
                clearable={editable}
              />
              <TextInput
                label={t('fuel.bunkering.fields.sampleLocation')}
                readOnly={!editable}
                value={values.sampleLocation}
                onChange={(e) => set('sampleLocation', e.currentTarget.value)}
              />
              <TextInput
                label={t('fuel.bunkering.fields.laboratory')}
                readOnly={!editable}
                value={values.laboratory}
                onChange={(e) => set('laboratory', e.currentTarget.value)}
              />
              <TextInput
                className={classes.span2}
                label={t('fuel.bunkering.fields.analysisResult')}
                readOnly={!editable}
                value={values.analysisResult}
                onChange={(e) => set('analysisResult', e.currentTarget.value)}
              />
              <Amount
                label={t('fuel.bunkering.fields.extraCost')}
                value={values.extraCost}
                readOnly={!editable}
                onChange={(v) => set('extraCost', v)}
                error={issue('extraCost')}
              />
              <TextInput
                label={t('fuel.bunkering.fields.invoiceNo')}
                readOnly={!editable}
                value={values.invoiceNo}
                onChange={(e) => set('invoiceNo', e.currentTarget.value)}
              />
              <Amount
                label={t('fuel.bunkering.fields.fxRate')}
                value={values.fxRate}
                readOnly={!editable}
                onChange={(v) => set('fxRate', v)}
                error={issue('fxRate')}
              />
              <DayInput
                label={t('fuel.bunkering.fields.fxDate')}
                readOnly={!editable}
                value={values.fxDate}
                onChange={(v) => set('fxDate', v)}
                clearable={editable}
              />
              <TextInput
                label={t('fuel.bunkering.fields.altComponent')}
                readOnly={!editable}
                value={values.altComponent}
                onChange={(e) => set('altComponent', e.currentTarget.value)}
              />
              <Amount
                label={t('fuel.bunkering.fields.altRatio')}
                value={values.altRatio}
                readOnly={!editable}
                onChange={(v) => set('altRatio', v)}
                error={issue('alternative.ratioPct')}
              />
              <TextInput
                label={t('fuel.bunkering.fields.altOrigin')}
                readOnly={!editable}
                value={values.altOrigin}
                onChange={(e) => set('altOrigin', e.currentTarget.value)}
              />
              <TextInput
                label={t('fuel.bunkering.fields.altCertificate')}
                readOnly={!editable}
                value={values.altCertificate}
                onChange={(e) => set('altCertificate', e.currentTarget.value)}
              />
              <Textarea
                className={classes.span5}
                label={t('fuel.bunkering.fields.differenceNote')}
                readOnly={!editable}
                autosize
                minRows={2}
                maxLength={500}
                value={values.differenceNote}
                onChange={(e) => set('differenceNote', e.currentTarget.value)}
              />
              <Textarea
                className={classes.span5}
                label={t('fuel.bunkering.fields.generalRemarks')}
                readOnly={!editable}
                autosize
                minRows={2}
                maxLength={500}
                value={values.remarks}
                onChange={(e) => set('remarks', e.currentTarget.value)}
              />
            </div>
          ) : null}
        </section>
      </div>
      {actions.length > 0 ? (
        <div className={classes.footer}>
          <WorkflowButtons
            actions={actions}
            busy={ledger.busy}
            onAction={(action) => {
              if (action === 'save') void ledger.save(input());
              else if (action === 'submit') void ledger.submit(input());
              else if (action === 'correct') void ledger.correct();
              else setModal(action);
            }}
          />
        </div>
      ) : null}
      <DecisionModal
        mode={modal}
        recordNo={detail?.recordNo ?? ''}
        onClose={() => setModal(null)}
        onConfirm={(decision) => (modal ? ledger.decide(modal, decision) : Promise.resolve())}
      />
    </section>
  );
}

export const EMPTY_CAN = {
  create: false,
  edit: false,
  submit: false,
  approve: false,
  return: false,
  void: false,
};

/** Ondalık giriş (metin; JS sayısına çevrilmez: ADR 0117 §11). */
export function Amount({
  label,
  value,
  onChange,
  readOnly,
  error,
  required,
  computed,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  readOnly: boolean;
  error?: string | null;
  required?: boolean;
  computed?: boolean;
}) {
  return (
    <TextInput
      className={computed ? classes.computed : undefined}
      label={label}
      withAsterisk={required ?? false}
      inputMode="decimal"
      readOnly={readOnly}
      value={value}
      onChange={(e) => onChange(e.currentTarget.value.replace(',', '.'))}
      error={error}
    />
  );
}

/** Tarih ve saat yan yana (tasarımdaki gibi takvim ve saat ikonuyla). */
export function DateTime({
  label,
  day,
  time,
  onDay,
  onTime,
  readOnly,
  error,
}: {
  label: string;
  day: string | null;
  time: string | null;
  onDay: (value: string | null) => void;
  onTime: (value: string) => void;
  readOnly: boolean;
  error?: string | null;
}) {
  return (
    <div>
      <div className={classes.dateTime}>
        <DayInput
          label={label}
          withAsterisk
          readOnly={readOnly}
          value={day}
          onChange={onDay}
          error={error}
        />
        <TimeInput
          label={<span style={{ visibility: 'hidden' }}>{label}</span>}
          aria-label={label}
          readOnly={readOnly}
          value={time}
          onChange={onTime}
        />
      </div>
    </div>
  );
}

export type { FuelRecordDetail };
